import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { QRCodeSVG } from 'qrcode.react';
import AccountService, { ProfileData } from '../../../services/account.service';

interface Props {
  profile: ProfileData | null;
  onUpdated: () => void;
}

const SecurityTab: React.FC<Props> = ({ profile, onUpdated }) => {
  const { t } = useTranslation();
  const [flow, setFlow] = useState<'idle' | 'totp' | 'email_otp'>('idle');
  const [totpData, setTotpData] = useState<{ secret: string; otpAuthUrl: string } | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const startTotpSetup = async () => {
    setLoading(true);
    try {
      const res: any = await AccountService.initiateTotpSetup();
      setTotpData(res.data ?? null);
      setFlow('totp');
    } catch {
      toast.error(t('account.security.setupError') as string);
    } finally {
      setLoading(false);
    }
  };

  const confirmTotpSetup = async () => {
    setLoading(true);
    try {
      const res: any = await AccountService.confirmTotpSetup(code);
      setBackupCodes(res.data?.backup_codes ?? []);
      setCode('');
      onUpdated();
    } catch {
      toast.error(t('account.security.invalidCode') as string);
    } finally {
      setLoading(false);
    }
  };

  const startEmailOtpSetup = async () => {
    setLoading(true);
    try {
      await AccountService.initiateEmailOtpSetup();
      setFlow('email_otp');
    } catch {
      toast.error(t('account.security.setupError') as string);
    } finally {
      setLoading(false);
    }
  };

  const confirmEmailOtpSetup = async () => {
    setLoading(true);
    try {
      const res: any = await AccountService.confirmEmailOtpSetup(code);
      setBackupCodes(res.data?.backup_codes ?? []);
      setCode('');
      onUpdated();
    } catch {
      toast.error(t('account.security.invalidCode') as string);
    } finally {
      setLoading(false);
    }
  };

  const disableMfa = async () => {
    if (!window.confirm(t('account.security.disableConfirm'))) return;
    setLoading(true);
    try {
      await AccountService.disableMfa();
      setFlow('idle');
      setTotpData(null);
      setBackupCodes(null);
      onUpdated();
      toast.success(t('account.security.mfaDisabled') as string);
    } catch {
      toast.error(t('account.security.setupError') as string);
    } finally {
      setLoading(false);
    }
  };

  const regenerateCodes = async () => {
    setLoading(true);
    try {
      const res: any = await AccountService.regenerateBackupCodes();
      setBackupCodes(res.data?.backup_codes ?? []);
      toast.success(t('account.security.codesRegenerated') as string);
    } catch {
      toast.error(t('account.security.setupError') as string);
    } finally {
      setLoading(false);
    }
  };

  const resetFlow = () => {
    setFlow('idle');
    setTotpData(null);
    setCode('');
    setBackupCodes(null);
  };

  if (backupCodes) {
    return <BackupCodesDisplay codes={backupCodes} onDone={resetFlow} />;
  }

  return (
    <div className="d-flex flex-column gap-4">
      {/* MFA status card */}
      <div className="card" style={{ borderRadius: '0.75rem' }}>
        <div className="card-header border-bottom pb-3 d-flex align-items-center justify-content-between">
          <h6 className="mb-0 fw-semibold">{t('account.security.mfaTitle')}</h6>
          {profile?.mfa_enabled && (
            <span className="badge bg-label-success">{t('account.security.enabled')}</span>
          )}
        </div>
        <div className="card-body">
          {profile?.mfa_enabled ? (
            <div>
              <p className="text-muted small mb-3">
                {t('account.security.currentMethod')}: <strong>{profile.mfa_method === 'totp'
                  ? t('account.security.methodTotp')
                  : t('account.security.methodEmail')}</strong>
              </p>
              <div className="d-flex gap-2 flex-wrap">
                <button className="btn btn-outline-danger btn-sm" onClick={disableMfa} disabled={loading}>
                  {t('account.security.disableMfa')}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={regenerateCodes} disabled={loading}>
                  {t('account.security.regenerateCodes')}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-muted small mb-3">{t('account.security.mfaDescription')}</p>
              {flow === 'idle' && (
                <div className="d-flex gap-2 flex-wrap">
                  <button className="btn btn-primary btn-sm" onClick={startTotpSetup} disabled={loading}>
                    <i className="ri-qr-code-line me-2" />{t('account.security.setupTotp')}
                  </button>
                  <button className="btn btn-outline-primary btn-sm" onClick={startEmailOtpSetup} disabled={loading}>
                    <i className="ri-mail-send-line me-2" />{t('account.security.setupEmailOtp')}
                  </button>
                </div>
              )}

              {flow === 'totp' && totpData && (
                <TotpSetupFlow
                  secret={totpData.secret}
                  otpAuthUrl={totpData.otpAuthUrl}
                  code={code}
                  onCodeChange={setCode}
                  onConfirm={confirmTotpSetup}
                  onCancel={resetFlow}
                  loading={loading}
                />
              )}

              {flow === 'email_otp' && (
                <EmailOtpSetupFlow
                  code={code}
                  onCodeChange={setCode}
                  onConfirm={confirmEmailOtpSetup}
                  onCancel={resetFlow}
                  loading={loading}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── TOTP flow ─────────────────────────────────────────────────────────────────

interface TotpFlowProps {
  secret: string;
  otpAuthUrl: string;
  code: string;
  onCodeChange: (v: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

const TotpSetupFlow: React.FC<TotpFlowProps> = ({ secret, otpAuthUrl, code, onCodeChange, onConfirm, onCancel, loading }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copySecret = () => {
    navigator.clipboard.writeText(secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="mt-3">
      <p className="small text-muted mb-3">{t('account.security.totpScanInstruction')}</p>
      <div className="d-flex align-items-start gap-4 flex-wrap mb-4">
        <div
          className="p-3 border rounded"
          style={{ borderRadius: '0.75rem', background: 'var(--bs-body-bg)' }}
        >
          <QRCodeSVG value={otpAuthUrl} size={160} />
        </div>
        <div>
          <div className="small text-muted mb-1">{t('account.security.manualSecret')}</div>
          <div className="d-flex align-items-center gap-2">
            <code className="small bg-light px-2 py-1 rounded" style={{ letterSpacing: '0.1em' }}>
              {secret}
            </code>
            <button className="btn btn-sm btn-light" onClick={copySecret} title="Copy">
              <i className={copied ? 'ri-check-line' : 'ri-clipboard-line'} />
            </button>
          </div>
        </div>
      </div>
      <label className="form-label small fw-medium">{t('account.security.enterCode')}</label>
      <div className="d-flex gap-2" style={{ maxWidth: 300 }}>
        <input
          className="form-control"
          placeholder="000000"
          maxLength={6}
          value={code}
          onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, ''))}
        />
        <button className="btn btn-primary" onClick={onConfirm} disabled={code.length !== 6 || loading}>
          {loading ? <span className="spinner-border spinner-border-sm" /> : t('common.verify')}
        </button>
      </div>
      <button className="btn btn-link btn-sm ps-0 mt-2 text-muted" onClick={onCancel}>
        {t('common.cancel')}
      </button>
    </div>
  );
};

// ── Email OTP flow ────────────────────────────────────────────────────────────

interface EmailOtpFlowProps {
  code: string;
  onCodeChange: (v: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

const EmailOtpSetupFlow: React.FC<EmailOtpFlowProps> = ({ code, onCodeChange, onConfirm, onCancel, loading }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-3">
      <p className="small text-muted mb-3">{t('account.security.emailOtpInstruction')}</p>
      <label className="form-label small fw-medium">{t('account.security.enterCode')}</label>
      <div className="d-flex gap-2" style={{ maxWidth: 300 }}>
        <input
          className="form-control"
          placeholder="000000"
          maxLength={6}
          value={code}
          onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, ''))}
        />
        <button className="btn btn-primary" onClick={onConfirm} disabled={code.length !== 6 || loading}>
          {loading ? <span className="spinner-border spinner-border-sm" /> : t('common.verify')}
        </button>
      </div>
      <button className="btn btn-link btn-sm ps-0 mt-2 text-muted" onClick={onCancel}>
        {t('common.cancel')}
      </button>
    </div>
  );
};

// ── Backup codes display ──────────────────────────────────────────────────────

interface BackupCodesProps {
  codes: string[];
  onDone: () => void;
}

const BackupCodesDisplay: React.FC<BackupCodesProps> = ({ codes, onDone }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copyAll = () => {
    navigator.clipboard.writeText(codes.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="card" style={{ borderRadius: '0.75rem' }}>
      <div className="card-header border-bottom pb-3">
        <h6 className="mb-0 fw-semibold">{t('account.security.backupCodesTitle')}</h6>
      </div>
      <div className="card-body">
        <div className="alert alert-warning py-2 mb-3 small">
          {t('account.security.backupCodesWarning')}
        </div>
        <div className="row g-2 mb-4">
          {codes.map((c, i) => (
            <div key={i} className="col-6 col-sm-4">
              <code
                className="d-block text-center py-2 border rounded small fw-medium"
                style={{ letterSpacing: '0.12em', borderRadius: '0.5rem' }}
              >
                {c}
              </code>
            </div>
          ))}
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={copyAll}>
            <i className={`${copied ? 'ri-check-line' : 'ri-clipboard-line'} me-2`} />
            {copied ? t('common.copied') : t('account.security.copyAllCodes')}
          </button>
          <button className="btn btn-primary btn-sm" onClick={onDone}>
            {t('account.security.doneWithCodes')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecurityTab;
